import { push } from 'redux-first-history';
import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import actions from './actions';
import cancellationPoliciesActions from './cancellationPolicy/actions';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import notifications from '../../../redux/notifications/actions';
import Api from '../../../helpers/api';
import { t } from '../../../i18n';

const agencyApi = new Api('agencies/:id');
const agencyContractApi = new Api('agency_contracts/:id');
const removeAgencyApi = new Api('agencies/:id/agency_contracts');
const cancellationPoliciesApi = new Api('agencies/:id/cancellation_policies');

// Flattens contract response (flag ON) into the legacy agency shape — promotes
// agency-level fields to top level and keeps contract-level metadata alongside.
function mapContractToAgency(contract) {
  return {
    ...contract.agency,
    contractId: contract.id,
    preference_level: contract.preference_level,
    status: contract.status,
    created_at: contract.created_at,
    starts_on: contract.starts_on,
    legal_entity: contract.legal_entity || null,
    actions: contract.actions || [],
    districts: contract.districts || [],
    invite: contract.invite || null,
  };
}

function* loadContractShow(contractId) {
  const result = yield call(sendRequest, agencyContractApi.load, { id: contractId }, notifications.REQUEST_ERROR);
  const contract = result?.data?.success?.data;

  return contract ? mapContractToAgency(contract) : null;
}

function* loadLegacyAgencyShow(agencyId) {
  const result = yield call(sendRequest, agencyApi.load, { id: agencyId }, notifications.REQUEST_ERROR);

  return result?.data?.success?.data?.agency || null;
}

function* loadData({ payload }) {
  try {
    const { id: urlId } = payload;
    const isLegalEntityEnabled = yield select(state =>
      Boolean(state.StartAppSettings?.feature_flags?.legal_entity_enabled),
    );

    const agency = isLegalEntityEnabled ? yield* loadContractShow(urlId) : yield* loadLegacyAgencyShow(urlId);

    if (!agency) {
      return;
    }

    const cancellationResult = yield call(
      sendRequest,
      cancellationPoliciesApi.load,
      { id: agency.id },
      notifications.REQUEST_ERROR,
    );
    const cancellationPolicies = cancellationResult?.data?.success?.data;

    if (cancellationPolicies) {
      yield put(actions.loadDataSuccess({ agency }));
      yield put(cancellationPoliciesActions.loadDataSuccess({ cancellationPolicyItems: cancellationPolicies.records }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* removeAgency({ payload }) {
  try {
    const { id, status } = payload;
    const result = yield call(sendRequest, removeAgencyApi.deleteItem, { id }, notifications.REQUEST_ERROR);

    if (result) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.deactivated_successfully', {
            item: t('dash.agencies.viewAgency.agency_label'),
          }),
        }),
      );
      yield put(push(`/agencies/${status === 'activated' ? 'active' : 'invite'}/`));
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actions.LOAD_REQUEST, loadData), takeEvery(actions.REMOVE_AGENCY_REQUEST, removeAgency)]);
}
